import {
  Box,
  VStack,
  Flex,
  Button,
  Divider,
  Grid,
  GridItem,
  FormControl,
  Input,
  FormErrorMessage,
  Tooltip,
  Icon,
  Text,
  HStack,
} from '@chakra-ui/react'
import { CustomRequiredInput } from 'components/input/input'
import { t } from 'i18next'
import _ from 'lodash'
import React, { useCallback, useState } from 'react'
import { Controller, useFieldArray, useFormContext, useWatch } from 'react-hook-form'
import { BiDownload, BiXCircle } from 'react-icons/bi'
import { IoMdAdd } from 'react-icons/io'
import NumberFormat from 'react-number-format'
import { ESTIMATE } from './estimate.i18n'
import { readFileContent } from 'api/vendor-details'
import { useUserRolesSelector } from 'utils/redux-common-selectors'
import { ESTIMATE_STATUS } from './wo-estimate-status'
import { isValidAndNonEmptyArray } from 'utils'
import { NEW_PROJECT } from 'features/vendor/projects/projects.i18n'

interface Props {
  vendor: any
  property: any
  workOrderId: any
  workOrder: any
  onDocumentModalOpen?: (e, index) => void
  vendorTrades: any
  estimate: any
  status: any
}

export const onFileChange = async file => {
  const fileContents = await readFileContent(file)
  const documentFile = {
    fileObjectContentType: file?.type,
    fileType: file?.name,
    fileObject: fileContents ?? '',
    documentType: 39,
  }
  return documentFile
}

export const downloadDocument = (link, text, testId) => {
  return (
    <a href={link} target="_blank" rel="noreferrer" download style={{ color: '#345EA6' }}>
      <HStack>
        <Icon as={BiDownload} size="sm" />
        <Text data-testid={testId} fontSize="12px" fontStyle="normal" maxW="70px" isTruncated>
          {text}
        </Text>
      </HStack>
    </a>
  )
}

export const EstimateLaborCost: React.FC<Props> = (props: any) => {
  const { vendor, property, workOrder, onDocumentModalOpen, vendorTrades, estimate,status } = props
  const isLicensedTrade = vendorTrades?.find(vt => vt.id === workOrder?.vendorSkillId)?.licensedSkill
  const clientRate = isLicensedTrade ? property?.client.licensedHourlyRate : property?.client?.billingRate
  //const vendorGeneralHourlyRate = vendor?.generalHourlyRate
  const { isVendor } = useUserRolesSelector()
  const vendorRate = isLicensedTrade ? workOrder?.vendorRate : vendor?.generalHourlyRate
  const [hoveredRow, setHoveredRow] = useState()

  const {
    control,
    formState: { errors },
    register,
    setValue,
    setError,
    clearErrors,
  } = useFormContext()

  const isFlatRate = useWatch({
    control,
    name: 'flatRate',
  })
  const {
    fields: laborCostFields,
    append: appendLaborCostField,
    remove: removelaborCostField,
  } = useFieldArray({
    control,
    name: 'laborCost',
  })
  const laborCost = useWatch({ name: 'laborCost', control })

  React.useEffect(() => {
    if (!isFlatRate) return
    laborCost?.forEach((l, i) => {
      setValue(`laborCost.${i}.itemCost`, 0)
      //setValue(`laborCost.${i}.itemVendorRate`, 0)
    })
  }, [isFlatRate])

  const addRow = useCallback(() => {
    appendLaborCostField({
      itemDesc: '',
      itemCost: 0,
      itemVendorRate: 0,
      itemClientRate: 0,
      itemType: 1,
      woId: workOrder?.id,
      itemAmount: 0,
      vendorHourlyRate: vendorRate,
      clientHourlyRate: clientRate,
      documents: [],
    })
  }, [appendLaborCostField])

  const removeRow = useCallback(
    (index: number) => {
      removelaborCostField(index)
    },
    [removelaborCostField],
  )

  const handleRowHover = index => {
    setHoveredRow(index)
  }

  const isCancelled = estimate?.estimateStatus === ESTIMATE_STATUS.CANCELLED

  const validateAtleastOnePicUploaded = () => {
    if (status?.label === 'CANCELLED') {
      return true
    }
    if (laborCost.every(row => !isValidAndNonEmptyArray(row?.documents)) && isVendor) {
      // HN-PSWOA-6052| Only to validate on vendor portal
      return 'At least one picture is required'
    }
    return true
  }

  return (
    <>
      <Box w="100%">
        <VStack alignItems="start" w="100%">
          <Flex w="100%" justifyContent={'space-between'}>
            <Box>
              <Flex flex="1">
                <Text
                  fontFamily="Poppins"
                  fontWeight="500"
                  fontSize="16px"
                  letterSpacing="0.5px"
                  color="#4A5568"
                  mt="12px"
                >
                  Labor Cost
                </Text>
                <Divider orientation="vertical" height="30px" border="1px solid #CBD5E0" marginLeft="10px" mt="8px" />
                <Button
                  position="relative"
                  top="10px"
                  variant="cubicle"
                  height="30px"
                  width='31px'
                  onClick={_.debounce(addRow, 250)}
                  color="darkPrimary.300"
                  fontSize="12px"
                  left='3px'
                  fontWeight="400"
                 
                  isDisabled={isCancelled}
                >
                  <Box
                    borderRadius="6px"
                    display="inline-flex"
                    alignItems="center"
                    justifyContent="center"
                    bg="#F2F3F4" 
                    width="18px"
                    height="18px"
                    marginRight="5px"
                    border="1px solid #CBD5E0"
                  >
                    <IoMdAdd color="darkPrimary.300" fontSize="14px" />
                  </Box>
                  New Row
                </Button>
              </Flex>
            </Box>
          </Flex>

          <Flex
            borderStyle="solid"
            borderColor="gray.300"
            borderWidth="1px 1px 1px 1px"
            flex="1"
            pos="relative"
            flexDirection="column"
            rounded={6}
            w="100%"
            minH="19vh"
            mt="0"
          >
            <Grid
              gridTemplateColumns={isVendor ? '10px 2fr 1fr 1.5fr' : '1px 2fr 1fr 1fr 1fr 1.5fr'}
              py="3"
              fontSize="14px"
              color="gray.600"
              bg="bgGlobal.50"
              gap="10px"
              borderWidth="0 0 px 0"
              borderStyle="solid"
              borderColor="gray.300"
              roundedTop={6}
            >
              <GridItem></GridItem>
              <GridItem ml={isVendor? '9px' : "2px"} fontWeight="500px" color="#4A5568" fontSize="14px">
                {  t(`${ESTIMATE}.description`)}
              </GridItem>

              {isVendor && isFlatRate ? (
                <GridItem fontWeight="500px" color="#4A5568" fontSize="14px" ml={isVendor ? '86px' :'203px'}>
                  Amount
                </GridItem>
              ) : (
                <GridItem fontWeight="500px" color="#4A5568" ml={isVendor? '100px' :'203px'} mr='38px' fontSize="14px" >
                  Hours
                </GridItem>
              )}
              {!isVendor && (
                <>
                  <GridItem fontWeight="500px" color="#4A5568" fontSize="14px">
                    Vendor
                  </GridItem>

                  <GridItem fontWeight="500px" color="#4A5568" ml='-9px' fontSize="14px">
                    Client
                  </GridItem>
                </>
              )}

              <GridItem fontWeight="500px" color="#4A5568" fontSize="14px" ml={isVendor ? '45px': '-20px'}>
                Image
              </GridItem>
            </Grid>

            <Box flex="1" overflow="auto" maxH="140px" mb="34px" id="amounts-list">
              {laborCostFields.map((laborCostField: any, index) => {
                return (
                  <Grid
                    className="amount-input-row"
                    key={index}
                    gridTemplateColumns={isVendor ? '328px 4.3fr 4fr 1fr' : '328px 4.3fr 4fr 4fr 6.5fr'}
                    fontSize="14px"
                    color="gray.600"
                    gap="10px"
                    mt="15px"
                    mb="15px"
                    ml="5px"
                    borderColor="gray.300"
                    height="auto"
                    onMouseEnter={() => handleRowHover(index)}
                    onMouseLeave={() => handleRowHover(null)}
                  >
                  

                    <GridItem>
                      <FormControl isInvalid={!!errors.laborCost?.[index]?.itemDesc}  ml ={ isVendor ? '25px': 'px'}>
                        <Tooltip label={laborCost?.[index]?.itemDesc}>
                          <Input
                            width='121%'
                            type="text"
                            size="sm"
                            autoComplete="off"
                            placeholder="Add Description here"
                            noOfLines={1}
                            variant="required-field"
                            {...register(`laborCost.${index}.itemDesc`, {
                              required: 'This is required field',
                            })}
                            isDisabled={isCancelled}
                            maxLength={2000}
                            onChange={e => {
                              setValue(`laborCost?.${index}?.itemDesc`, (e?.target as HTMLInputElement).value)
                              if ((e.target as HTMLInputElement).value?.length > 255) {
                                setError(`laborCost.${index}.itemDesc`, {
                                  type: 'custom',
                                  message: t(`${NEW_PROJECT}.fieldLimitReached`),
                                })
                              } else {
                                clearErrors(`laborCost.${index}.itemDesc`)
                              }
                            }}
                          />
                        </Tooltip>
                        <FormErrorMessage>{errors?.laborCost?.[index]?.itemDesc?.message ?? ''}</FormErrorMessage>
                      </FormControl>
                    </GridItem>

                    <GridItem display={`${isVendor && isFlatRate ? 'none' : ''}`}>
                      <FormControl isInvalid={!!errors.transaction?.[index]?.itemCost}  ml ={ isVendor ? '145px': '69px'} maxW="80px">
                        <Controller
                          name={`laborCost.${index}.itemCost` as const}
                          control={control}
                          rules={{
                            required: isFlatRate ? false : 'This is required field',
                          }}
                          render={({ field, fieldState }) => {
                            return (
                              <>
                                <NumberFormat
                                  customInput={CustomRequiredInput}
                                  value={field.value}
                                  onKeyDown={e => {
                                    if (e?.code === 'Minus') {
                                      e.preventDefault()
                                    }
                                  }}
                                  onValueChange={e => {
                                    const noOfHours = e.floatValue ?? ''
                                    if (noOfHours !== '') {
                                      setValue(`laborCost.${index}.itemVendorRate`, noOfHours * vendorRate)
                                      setValue(`laborCost.${index}.itemClientRate`, noOfHours * clientRate)
                                    }

                                    field.onChange(noOfHours ?? '')
                                  }}
                                  variant={'required-field'}
                                  size="sm"
                                  isDisabled={isFlatRate || isCancelled}
                                />
                                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                              </>
                            )
                          }}
                        />
                      </FormControl>
                    </GridItem>

                    <GridItem display={`${isVendor && !isFlatRate ? 'none' : ''}`}>
                      <FormControl isInvalid={!!errors.transaction?.[index]?.itemVendorRate} ml={isVendor? '145px': '40px'} maxW="80px">
                        <Controller
                          name={`laborCost.${index}.itemVendorRate` as const}
                          control={control}
                          rules={{
                            required: 'This is required field',
                          }}
                          render={({ field, fieldState }) => {
                            return (
                              <>
                                <NumberFormat
                                  customInput={Input}
                                  value={field.value}
                                  onKeyDown={e => {
                                    if (e?.code === 'Minus') {
                                      e.preventDefault()
                                    }
                                  }}
                                  prefix={'$'}
                                  onValueChange={e => {
                                    field.onChange(e.floatValue ?? '')
                                  }}
                                  variant={'required-field'}
                                  size="sm"
                                  isDisabled={isCancelled}
                                  maxLength={16}
                                />
                                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                              </>
                            )
                          }}
                        />
                      </FormControl>
                    </GridItem>

                    <GridItem display={`${isVendor ? 'none' : ''}`}>
                      <FormControl isInvalid={!!errors.transaction?.[index]?.itemClientRate}  ml='20px' maxW="80px">
                        <Controller
                          name={`laborCost.${index}.itemClientRate` as const}
                          control={control}
                          rules={{
                            required: 'This is required field',
                          }}
                          render={({ field, fieldState }) => {
                            return (
                              <>
                                <NumberFormat
                                  customInput={Input}
                                  value={field.value}
                                  onKeyDown={e => {
                                    if (e?.code === 'Minus') {
                                      e.preventDefault()
                                    }
                                  }}
                                  prefix={'$'}
                                  onValueChange={e => {
                                    field.onChange(e.floatValue ?? '')
                                  }}
                                  variant={'required-field'}
                                  size="sm"
                                  isDisabled={isCancelled}
                                  maxLength={16}
                                />
                                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                              </>
                            )
                          }}
                        />
                      </FormControl>
                    </GridItem>

                    <GridItem>
                      <FormControl isInvalid={!!errors.laborCost?.[index]?.documents} ml={isVendor? '45px': '0px'}>
                        <Controller
                          name={`laborCost.${index}.documents` as const}
                          control={control}
                          rules={{
                            validate: validateAtleastOnePicUploaded,
                          }}
                          render={({ field, fieldState }) => {
                            return (
                              <>
                                <Button
                                  variant="outline"
                                  colorScheme="brand"
                                  size="sm"
                                  onClick={e => {
                                    onDocumentModalOpen(e, index)
                                  }}
                                  isDisabled={isCancelled}
                                >
                                  {laborCost?.[index]?.documents?.length > 0 ? t('viewFiles') : t('chooseFile')}
                                </Button>
                                <FormErrorMessage>{fieldState.error?.message}</FormErrorMessage>
                              </>
                            )
                          }}
                        />
                      </FormControl>
                    </GridItem>
                    <GridItem w="20px" position="relative">
                      {index !== 0 && hoveredRow === index && !isCancelled && (
                        <Icon
                          as={BiXCircle}
                          boxSize={5}
                          color="brand.300"
                          onClick={() => {
                            removeRow(index)
                          }}
                          cursor="pointer"
                          position="absolute"
                          left="786px"
                          marginTop='-35px'
                        
                        />
                      )}
                    </GridItem>
                  </Grid>
                )
              })}
            </Box>
          </Flex>
        </VStack>
      </Box>
    </>
  )
}
